import React from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Twitter, Instagram, Mail, MapPin, Phone } from 'lucide-react';
import TEXT_CONSTANTS from '../constants/text';

const Footer: React.FC = () => {
  return (
    <footer className="bg-gray-900 dark:bg-secondary-950 text-white transition-colors hidden sm:block">
      <div className="max-w-7xl mx-auto px-4 py-8 sm:py-12 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {/* Company Info */}
          <div className="sm:col-span-2 lg:col-span-1">
            <h3 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4 text-primary-400">
              {TEXT_CONSTANTS.BRAND.WEBSITE_NAME_SHORT}
            </h3>
            <p className="text-gray-300 dark:text-gray-400 mb-4 text-sm sm:text-base leading-relaxed">
              Your one-stop destination for all your shopping needs. Quality products,
              fast delivery, and excellent customer service.
            </p>
            
            {/* Contact Info */}
            <div className="space-y-2 mb-4">
              <div className="flex items-center text-sm text-gray-300 dark:text-gray-400">
                <Mail className="h-4 w-4 mr-2 flex-shrink-0" />
                <span>support@eshop.com</span>
              </div>
              <div className="flex items-center text-sm text-gray-300 dark:text-gray-400">
                <Phone className="h-4 w-4 mr-2 flex-shrink-0" />
                <span>+91 12345 67890</span>
              </div>
              <div className="flex items-start text-sm text-gray-300 dark:text-gray-400">
                <MapPin className="h-4 w-4 mr-2 flex-shrink-0 mt-0.5" />
                <span>123 Shopping Street, Mumbai, India</span>
              </div>
            </div>

            {/* Social Links */}
            <div className="flex space-x-4">
              <a 
                href="https://facebook.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-primary-400 transition-colors p-2 rounded-lg hover:bg-gray-800 dark:hover:bg-secondary-800"
                aria-label="Facebook"
              >
                <Facebook className="h-5 w-5" />
              </a>
              <a 
                href="https://twitter.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-primary-400 transition-colors p-2 rounded-lg hover:bg-gray-800 dark:hover:bg-secondary-800"
                aria-label="Twitter"
              >
                <Twitter className="h-5 w-5" />
              </a>
              <a 
                href="https://instagram.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-primary-400 transition-colors p-2 rounded-lg hover:bg-gray-800 dark:hover:bg-secondary-800"
                aria-label="Instagram"
              >
                <Instagram className="h-5 w-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4 text-white">
              Quick Links
            </h4>
            <ul className="space-y-2">
              <li>
                <Link 
                  to="/" 
                  className="text-gray-300 dark:text-gray-400 hover:text-primary-400 text-sm sm:text-base transition-colors"
                >
                  Home
                </Link>
              </li>
              <li>
                <Link 
                  to="/products" 
                  className="text-gray-300 dark:text-gray-400 hover:text-primary-400 text-sm sm:text-base transition-colors"
                >
                  Products
                </Link>
              </li>
              <li>
                <Link 
                  to="/cart" 
                  className="text-gray-300 dark:text-gray-400 hover:text-primary-400 text-sm sm:text-base transition-colors"
                >
                  Cart
                </Link>
              </li>
              <li>
                <Link 
                  to="/orders" 
                  className="text-gray-300 dark:text-gray-400 hover:text-primary-400 text-sm sm:text-base transition-colors"
                >
                  My Orders
                </Link>
              </li>
            </ul>
          </div>

          {/* Customer Service */}
          <div>
            <h4 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4 text-white">
              Customer Service
            </h4>
            <ul className="space-y-2">
              <li>
                <a 
                  href="/contact" 
                  className="text-gray-300 dark:text-gray-400 hover:text-primary-400 text-sm sm:text-base transition-colors"
                >
                  Contact Us
                </a>
              </li>
              <li>
                <a 
                  href="/shipping" 
                  className="text-gray-300 dark:text-gray-400 hover:text-primary-400 text-sm sm:text-base transition-colors"
                >
                  Shipping Info
                </a>
              </li>
              <li>
                <a 
                  href="/returns" 
                  className="text-gray-300 dark:text-gray-400 hover:text-primary-400 text-sm sm:text-base transition-colors"
                >
                  Returns & Refunds
                </a>
              </li>
              <li>
                <a 
                  href="/size-guide" 
                  className="text-gray-300 dark:text-gray-400 hover:text-primary-400 text-sm sm:text-base transition-colors"
                >
                  Size Guide
                </a>
              </li>
              <li>
                <a 
                  href="/faq" 
                  className="text-gray-300 dark:text-gray-400 hover:text-primary-400 text-sm sm:text-base transition-colors"
                >
                  FAQ
                </a>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4 text-white">
              Legal
            </h4>
            <ul className="space-y-2">
              <li>
                <a 
                  href="/privacy" 
                  className="text-gray-300 dark:text-gray-400 hover:text-primary-400 text-sm sm:text-base transition-colors"
                >
                  Privacy Policy
                </a>
              </li>
              <li>
                <a 
                  href="/terms" 
                  className="text-gray-300 dark:text-gray-400 hover:text-primary-400 text-sm sm:text-base transition-colors"
                >
                  Terms of Service
                </a>
              </li>
              <li>
                <a 
                  href="/cookies" 
                  className="text-gray-300 dark:text-gray-400 hover:text-primary-400 text-sm sm:text-base transition-colors"
                >
                  Cookie Policy
                </a>
              </li>
              <li>
                <a 
                  href="/security" 
                  className="text-gray-300 dark:text-gray-400 hover:text-primary-400 text-sm sm:text-base transition-colors"
                >
                  Security
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Newsletter Signup */}
        <div className="border-t border-gray-700 dark:border-secondary-800 mt-8 pt-6 sm:pt-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex-1">
              <h4 className="text-base sm:text-lg font-semibold mb-2 text-white">
                Stay Updated
              </h4>
              <p className="text-sm text-gray-300 dark:text-gray-400">
                Subscribe to get special offers, free giveaways, and updates.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 min-w-0 sm:min-w-80">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-4 py-2 bg-gray-800 dark:bg-secondary-800 border border-gray-600 dark:border-secondary-600 
                         rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 
                         focus:border-transparent text-sm transition-colors"
              />
              <button className="bg-primary-600 hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 
                               text-white px-4 py-2 rounded-lg font-medium text-sm transition-colors whitespace-nowrap">
                Subscribe
              </button>
            </div>
          </div>
        </div>

        {/* Copyright */}
        <div className="border-t border-gray-700 dark:border-secondary-800 mt-6 sm:mt-8 pt-6 sm:pt-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 text-center sm:text-left">
            <p className="text-gray-300 dark:text-gray-400 text-sm">
              {TEXT_CONSTANTS.BRAND.COPYRIGHT}
            </p>
            <div className="flex flex-wrap justify-center sm:justify-end gap-4 text-sm">
              <span className="text-gray-400">🔒 Secure Shopping</span>
              <span className="text-gray-400">📦 Fast Delivery</span>
              <span className="text-gray-400">💯 Quality Guaranteed</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;